import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Mail, Phone, MapPin, Calendar, Camera, Save, AlertCircle,
  Check, Loader2, Shield, ChevronRight, Upload, FileText, CheckCircle,
  CreditCard, Lock, Eye, EyeOff
} from 'lucide-react';
import { apiRequest, cn } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

export default function Profile() {
  const navigate = useNavigate();
  const { user, loadUser } = useAuth();
  const fileInputRef = useRef(null);
  const idFrontInputRef = useRef(null);
  const idBackInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [needsReview, setNeedsReview] = useState(false);
  const [idVerification, setIdVerification] = useState(null);
  const [pwData, setPwData] = useState({ current: '', newPw: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    date_of_birth: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: '',
  });

  useEffect(() => {
    fetchProfile();
    fetchIdVerification();
  }, []);

  const fetchProfile = async () => {
    try {
      const profile = await apiRequest(`/users/profile/${user.id}`);
      setProfileData(profile);
      
      const info = profile.personal_info || {};
      const emergency = info.emergency_contact || {};
      
      setFormData({
        name: profile.name || info.name || '',
        phone: info.phone || '',
        address: info.address || '',
        date_of_birth: info.date_of_birth || '',
        emergency_contact_name: emergency.name || '',
        emergency_contact_phone: emergency.phone || '',
        emergency_contact_relationship: emergency.relationship || '',
      });
      
      // Check if profile needs review (not reviewed in 90 days)
      const reviewedAt = profile.user?.profile_reviewed_at;
      if (!reviewedAt) {
        setNeedsReview(true);
      } else {
        const reviewDate = new Date(reviewedAt);
        const daysSinceReview = (new Date() - reviewDate) / (1000 * 60 * 60 * 24);
        setNeedsReview(daysSinceReview > 90);
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchIdVerification = async () => {
    try {
      const data = await apiRequest('/users/me/id-verification');
      setIdVerification(data);
    } catch (error) {
      console.log('No ID verification found');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Derive personal_info early so helpers below (validateForm / completion) can use it safely.
  const info = profileData?.personal_info || {};

  const validateForm = () => {
    const errors = [];
    if (!info.profile_photo) errors.push('Profile Photo (Selfie)');
    if (!formData.phone.trim()) errors.push('Phone Number');
    if (!formData.address.trim()) errors.push('Current Address');
    if (!formData.date_of_birth.trim()) errors.push('Date of Birth');
    if (!formData.emergency_contact_name.trim()) errors.push('Emergency Contact Name');
    if (!formData.emergency_contact_phone.trim()) errors.push('Emergency Contact Phone');
    if (!formData.emergency_contact_relationship.trim()) errors.push('Emergency Contact Relationship');
    return errors;
  };

  // Calculate profile completion percentage (70 points for profile, 30 points for ID)
  const calculateProfileCompletion = () => {
    // Profile fields (70 points total - 10 points each)
    const profileFields = [
      info.profile_photo,
      formData.phone.trim(),
      formData.address.trim(),
      formData.date_of_birth.trim(),
      formData.emergency_contact_name.trim(),
      formData.emergency_contact_phone.trim(),
      formData.emergency_contact_relationship.trim()
    ];
    const profileFilled = profileFields.filter(Boolean).length;
    const profilePoints = Math.round((profileFilled / profileFields.length) * 70);
    
    // ID Verification (30 points)
    const idVerified = idVerification?.status === 'verified' || idVerification?.status === 'pending';
    const idPoints = idVerified ? 30 : 0;
    
    return profilePoints + idPoints;
  };

  const handleIdUpload = async (type, file) => {
    if (!file) return;

    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'].includes(file.type)) {
      toast.error('Please upload a JPG, PNG, WebP image or PDF');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be less than 10MB');
      return;
    }

    setUploadingId(type);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;
        
        await apiRequest('/users/me/id-verification', {
          method: 'POST',
          body: JSON.stringify({
            [type]: base64,
            file_name: file.name
          })
        });
        
        toast.success(`ID ${type === 'front_image' ? 'front' : 'back'} uploaded`);
        fetchIdVerification();
        setUploadingId(null);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload ID');
      setUploadingId(null);
    }
  };

  const handleSaveProfile = async () => {
    // Required fields are flagged with red asterisks for visibility, BUT
    // we still allow saving with partial data — the user can come back any
    // time and finish their profile. Completion % keeps them honest.
    const missing = validateForm();

    setSaving(true);
    try {
      await apiRequest('/users/me/profile', {
        method: 'PUT',
        body: JSON.stringify(formData)
      });
      if (missing.length > 0) {
        toast.success(
          `Saved. Still missing: ${missing.join(', ')}. You can complete it later.`,
          { duration: 6000 }
        );
      } else {
        toast.success('Profile updated successfully');
      }
      // Refresh this page's profile AND the AuthContext so sidebar/header/mobile-nav
      // all show the newly saved name immediately (prevents stale name display).
      await fetchProfile();
      try { await loadUser?.(); } catch (_) { /* non-fatal */ }
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmReview = async () => {
    try {
      await apiRequest('/users/me/confirm-profile-review', { method: 'POST' });
      setNeedsReview(false);
      toast.success('Profile review confirmed');
    } catch (error) {
      toast.error('Failed to confirm review');
    }
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Please upload a JPG, PNG or WebP image');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64 = event.target.result;

        try {
          const res = await apiRequest('/users/me/profile-photo', {
            method: 'PUT',
            body: JSON.stringify({ photo_url: base64 })
          });

          const newPhotoUrl = res?.photo_url || base64;

          // Update only the profile photo locally. Do NOT re-fetch the full
          // profile, otherwise in-progress form fields would be overwritten
          // with stale DB values and the user loses their typed data.
          setProfileData(prev => prev ? {
            ...prev,
            personal_info: { ...(prev.personal_info || {}), profile_photo: newPhotoUrl }
          } : prev);

          // Refresh the auth context so header avatars / menus update too
          try { await loadUser?.(); } catch (_) { /* non-fatal */ }

          toast.success('Profile photo updated');
        } catch (err) {
          toast.error('Failed to upload photo');
        } finally {
          setUploadingPhoto(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload photo');
      setUploadingPhoto(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwData.newPw !== pwData.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (pwData.newPw.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    setPwSaving(true);
    try {
      await apiRequest('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ current_password: pwData.current, new_password: pwData.newPw }),
      });
      toast.success('Password changed successfully');
      setPwData({ current: '', newPw: '', confirm: '' });
    } catch (error) {
      toast.error(error.message || 'Failed to change password. Check your current password.');
    } finally {
      setPwSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#DC2626]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="profile-page">
      {/* Review Banner */}
      {needsReview && (
        <Card className="border-orange-300 bg-orange-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-orange-800">Quarterly Profile Review Required</h3>
                <p className="text-sm text-orange-700 mt-1">
                  Please review your personal information, address, and emergency contact details. 
                  Update any changes and confirm your review.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Profile Completion Progress */}
      <Card className={calculateProfileCompletion() < 100 ? "border-amber-300 bg-amber-50" : "border-green-300 bg-green-50"}>
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <h3 className={`font-semibold ${calculateProfileCompletion() < 100 ? 'text-amber-800' : 'text-green-800'}`}>
                  Profile Completion
                </h3>
                <span className={`text-lg font-bold ${calculateProfileCompletion() < 100 ? 'text-amber-700' : 'text-green-700'}`}>
                  {calculateProfileCompletion()}%
                </span>
              </div>
              <div className="w-full bg-white/50 rounded-full h-2.5">
                <div 
                  className={`h-2.5 rounded-full transition-all duration-500 ${calculateProfileCompletion() < 100 ? 'bg-amber-500' : 'bg-green-500'}`}
                  style={{ width: `${calculateProfileCompletion()}%` }}
                />
              </div>
              {calculateProfileCompletion() < 100 && (
                <p className="text-xs text-amber-700 mt-2">
                  Complete all required fields including your profile photo to reach 100%
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Profile Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {/* Profile Photo */}
            <div className="relative">
              <div 
                className={cn(
                  "w-28 h-28 rounded-full overflow-hidden bg-slate-100 cursor-pointer",
                  "hover:opacity-80 transition-opacity shadow-lg",
                  !info.profile_photo ? "border-4 border-red-400" : "border-4 border-white"
                )}
                onClick={handlePhotoClick}
              >
                {info.profile_photo ? (
                  <img 
                    src={info.profile_photo} 
                    alt={info.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-[#DC2626] flex items-center justify-center">
                    <span className="text-4xl font-bold text-white">
                      {info.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                {uploadingPhoto && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-white" />
                  </div>
                )}
              </div>
              <button
                className="absolute bottom-0 right-0 p-2 bg-[#DC2626] rounded-full text-white hover:bg-[#B91C1C] shadow-lg"
                onClick={handlePhotoClick}
              >
                <Camera className="h-4 w-4" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="hidden"
                onChange={handlePhotoChange}
              />
            </div>

            {/* User Info */}
            <div className="text-center sm:text-left flex-1">
              <Input
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="text-2xl font-bold text-slate-900 border-0 border-b-2 border-transparent hover:border-slate-200 focus:border-indigo-500 px-0 h-auto py-1 rounded-none bg-transparent"
                data-testid="profile-name-input"
              />
              <p className="text-slate-500">{info.position || info.role}</p>
              <div className="flex flex-wrap gap-2 mt-2 justify-center sm:justify-start">
                <Badge className="bg-green-100 text-green-800">{info.status}</Badge>
                {info.store && (
                  <Badge variant="outline">{info.store}</Badge>
                )}
              </div>
            </div>
          </div>

          <p className="text-sm text-center mt-4">
            {!info.profile_photo ? (
              <span className="text-red-600 font-medium">
                Profile photo required <span className="text-red-500">*</span> - Click to upload a closeup selfie
              </span>
            ) : (
              <span className="text-slate-500">Click on your photo to upload a new selfie</span>
            )}
          </p>
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-[#DC2626]" />
            Contact Information
          </CardTitle>
          <CardDescription>Keep your contact details up to date</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input 
                id="email" 
                value={info.email || ''} 
                disabled 
                className="bg-slate-50"
              />
              <p className="text-xs text-slate-500">Contact admin to change email</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number <span className="text-red-500">*</span></Label>
              <Input 
                id="phone" 
                name="phone"
                value={formData.phone} 
                onChange={handleInputChange}
                placeholder="Enter your phone number"
                className={!formData.phone.trim() ? 'border-red-300' : ''}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address">Current Address <span className="text-red-500">*</span></Label>
              <Input 
                id="address" 
                name="address"
                value={formData.address} 
                onChange={handleInputChange}
                placeholder="Enter your current address"
                className={!formData.address.trim() ? 'border-red-300' : ''}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="date_of_birth">Date of Birth <span className="text-red-500">*</span></Label>
              <Input 
                id="date_of_birth" 
                name="date_of_birth"
                type="date"
                value={formData.date_of_birth} 
                onChange={handleInputChange}
                className={!formData.date_of_birth.trim() ? 'border-red-300' : ''}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Emergency Contact */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-500" />
            Emergency Contact
          </CardTitle>
          <CardDescription>This person will be contacted in case of emergency</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4">
            <div className="space-y-2">
              <Label htmlFor="emergency_contact_name">Contact Name <span className="text-red-500">*</span></Label>
              <Input 
                id="emergency_contact_name" 
                name="emergency_contact_name"
                value={formData.emergency_contact_name} 
                onChange={handleInputChange}
                placeholder="Full name of emergency contact"
                className={!formData.emergency_contact_name.trim() ? 'border-red-300' : ''}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="emergency_contact_phone">Phone Number <span className="text-red-500">*</span></Label>
                <Input 
                  id="emergency_contact_phone" 
                  name="emergency_contact_phone"
                  value={formData.emergency_contact_phone} 
                  onChange={handleInputChange}
                  placeholder="Phone number"
                  className={!formData.emergency_contact_phone.trim() ? 'border-red-300' : ''}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="emergency_contact_relationship">Relationship <span className="text-red-500">*</span></Label>
                <Input 
                  id="emergency_contact_relationship" 
                  name="emergency_contact_relationship"
                  value={formData.emergency_contact_relationship} 
                  onChange={handleInputChange}
                  placeholder="e.g., Spouse, Parent"
                  className={!formData.emergency_contact_relationship.trim() ? 'border-red-300' : ''}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Button 
          className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C]"
          onClick={handleSaveProfile}
          disabled={saving}
        >
          {saving ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Save Changes
        </Button>
        
        {needsReview && (
          <Button 
            variant="outline"
            className="flex-1 border-green-500 text-green-700 hover:bg-green-50"
            onClick={handleConfirmReview}
          >
            <Check className="h-4 w-4 mr-2" />
            Confirm Profile Review
          </Button>
        )}
      </div>

      {/* ID Verification Section - 30 Points */}
      <Card className={!idVerification?.front_image ? "border-amber-300" : idVerification?.status === 'verified' ? "border-green-300" : ""}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-blue-600" />
            ID Verification
            <Badge className={
              idVerification?.status === 'verified' ? "bg-green-100 text-green-800" :
              idVerification?.status === 'pending' ? "bg-amber-100 text-amber-800" :
              "bg-slate-100 text-slate-800"
            }>
              {idVerification?.status === 'verified' ? 'Verified' : 
               idVerification?.status === 'pending' ? 'Pending Review' : 
               'Not Submitted'} • 30 Points
            </Badge>
          </CardTitle>
          <CardDescription>
            Upload your government-issued ID (Driver's License, Passport, or Medicare Card)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Front of ID */}
            <div className="space-y-2">
              <Label>Front of ID <span className="text-red-500">*</span></Label>
              <div 
                className={cn(
                  "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors",
                  idVerification?.front_image ? "border-green-300 bg-green-50" : "border-slate-300 hover:border-blue-400"
                )}
                onClick={() => idFrontInputRef.current?.click()}
              >
                {uploadingId === 'front_image' ? (
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-blue-600" />
                ) : idVerification?.front_image ? (
                  <div className="space-y-2">
                    <CheckCircle className="h-8 w-8 mx-auto text-green-600" />
                    <p className="text-sm text-green-700">Front uploaded</p>
                    <p className="text-xs text-slate-500">Click to replace</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="h-8 w-8 mx-auto text-slate-400" />
                    <p className="text-sm text-slate-600">Click to upload front</p>
                    <p className="text-xs text-slate-400">JPG, PNG, WebP or PDF (max 10MB)</p>
                  </div>
                )}
              </div>
              <input
                ref={idFrontInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                className="hidden"
                onChange={(e) => handleIdUpload('front_image', e.target.files?.[0])}
              />
            </div>

            {/* Back of ID */}
            <div className="space-y-2">
              <Label>Back of ID (if applicable)</Label>
              <div 
                className={cn(
                  "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors",
                  idVerification?.back_image ? "border-green-300 bg-green-50" : "border-slate-300 hover:border-blue-400"
                )}
                onClick={() => idBackInputRef.current?.click()}
              >
                {uploadingId === 'back_image' ? (
                  <Loader2 className="h-8 w-8 mx-auto animate-spin text-blue-600" />
                ) : idVerification?.back_image ? (
                  <div className="space-y-2">
                    <CheckCircle className="h-8 w-8 mx-auto text-green-600" />
                    <p className="text-sm text-green-700">Back uploaded</p>
                    <p className="text-xs text-slate-500">Click to replace</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Upload className="h-8 w-8 mx-auto text-slate-400" />
                    <p className="text-sm text-slate-600">Click to upload back</p>
                    <p className="text-xs text-slate-400">Optional for some IDs</p>
                  </div>
                )}
              </div>
              <input
                ref={idBackInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                className="hidden"
                onChange={(e) => handleIdUpload('back_image', e.target.files?.[0])}
              />
            </div>
          </div>

          {idVerification?.status === 'pending' && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <p className="text-sm text-amber-700">
                Your ID is being reviewed. You'll be notified once verified.
              </p>
            </div>
          )}

          {idVerification?.status === 'verified' && (
            <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <p className="text-sm text-green-700">
                Your ID has been verified. Thank you!
              </p>
            </div>
          )}

          {!idVerification?.front_image && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <p className="text-sm text-amber-700">
                Upload your ID to earn 30 points towards profile completion.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Employment Info (Read Only) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-slate-500" />
            Employment Information
          </CardTitle>
          <CardDescription>Contact HR to update this information</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Employment Type - Prominent Display */}
          <div className="mb-4 p-4 rounded-lg bg-gradient-to-r from-slate-100 to-slate-50 border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Employment Type</p>
                <p className="text-xl font-bold text-slate-900">
                  {info.employment_type === 'full_time' ? 'Full-Time' :
                   info.employment_type === 'part_time' ? 'Part-Time' :
                   'Casual'}
                </p>
              </div>
              {(info.employment_type === 'full_time' || info.employment_type === 'part_time') && (
                <Badge className={
                  info.employment_type === 'full_time' ? 'bg-green-100 text-green-800 text-sm px-3' :
                  'bg-blue-100 text-blue-800 text-sm px-3'
                }>
                  Paid Leave Eligible
                </Badge>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Employee ID:</span>
              <p className="font-medium">{info.employee_id || 'Not Set'}</p>
            </div>
            <div>
              <span className="text-slate-500">Status:</span>
              <Badge className={
                info.status === 'active' ? 'bg-green-100 text-green-800' :
                info.status === 'onboarding' ? 'bg-amber-100 text-amber-800' :
                info.status === 'inactive' ? 'bg-red-100 text-red-800' :
                'bg-slate-100 text-slate-800'
              }>
                {info.status || 'Active'}
              </Badge>
            </div>
            <div>
              <span className="text-slate-500">Position:</span>
              <p className="font-medium">{info.position || 'Not Set'}</p>
            </div>
            <div>
              <span className="text-slate-500">Store:</span>
              <p className="font-medium">{info.store || 'Not Set'}</p>
            </div>
            <div>
              <span className="text-slate-500">Store/Site:</span>
              <p className="font-medium">{info.store || 'Not Set'}</p>
            </div>
            <div>
              <span className="text-slate-500">Hourly Rate:</span>
              <p className="font-medium">{info.hourly_rate ? `$${info.hourly_rate}/hr` : 'Not Set'}</p>
            </div>
            <div>
              <span className="text-slate-500">Start Date:</span>
              <p className="font-medium">
                {info.start_date ? new Date(info.start_date).toLocaleDateString() : 'Not Set'}
              </p>
            </div>
          </div>

          {/* Job Responsibilities */}
          {info.job_responsibilities && (
            <div className="mt-4 pt-4 border-t">
              <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Job Responsibilities
              </h4>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{info.job_responsibilities}</p>
              </div>
            </div>
          )}

          {/* Leave Balance - Only for Full-Time and Part-Time */}
          {(info.employment_type === 'full_time' || info.employment_type === 'part_time') && (
            <div className="mt-4 pt-4 border-t">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Leave Balances</h4>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-green-700">{info.leave_balance?.annual || 0}</p>
                  <p className="text-xs text-green-600">Annual Leave</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-blue-700">{info.leave_balance?.personal || 0}</p>
                  <p className="text-xs text-blue-600">Personal Leave</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-purple-700">{info.leave_balance?.long_service || 0}</p>
                  <p className="text-xs text-purple-600">Long Service</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password Section */}
      <Card data-testid="change-password-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lock className="h-5 w-5 text-slate-600" />
            Change Password
          </CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <div>
              <Label htmlFor="current-pw">Current Password</Label>
              <div className="relative mt-1">
                <Input
                  id="current-pw"
                  type={showPw ? 'text' : 'password'}
                  value={pwData.current}
                  onChange={e => setPwData(p => ({ ...p, current: e.target.value }))}
                  required
                  data-testid="current-password-input"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="new-pw">New Password</Label>
              <Input
                id="new-pw"
                type={showPw ? 'text' : 'password'}
                value={pwData.newPw}
                onChange={e => setPwData(p => ({ ...p, newPw: e.target.value }))}
                required
                minLength={6}
                className="mt-1"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <Label htmlFor="confirm-pw">Confirm New Password</Label>
              <Input
                id="confirm-pw"
                type={showPw ? 'text' : 'password'}
                value={pwData.confirm}
                onChange={e => setPwData(p => ({ ...p, confirm: e.target.value }))}
                required
                minLength={6}
                className="mt-1"
                data-testid="confirm-password-input"
              />
            </div>
            <Button type="submit" disabled={pwSaving || !pwData.current || !pwData.newPw || !pwData.confirm} data-testid="change-password-submit">
              {pwSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
              {pwSaving ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
